import {
  setPaymentFees,
  setPaymentID,
  setSignatoryName,
} from '../payment/paymentSlice';
import {
  finishApplication,
  setApplicationError,
  setApplyApplicationNumber,
  startApplication,
} from './applySlice';
import { getCustomBreakdown } from '../../utils';
import { AppThunk, RootState } from 'src/store/store';
import api from 'src/api';
import { ChargeableFlow, RequiredHiddable } from 'src/types/configuration';
import { ApplicationData, Document, UploadConfiguration } from 'src/types/flow';
import { Dispatch } from '@reduxjs/toolkit';
import { checkPaymentRequired } from 'src/common/licenseUtils';
import { setCurrentStep, setStepCompletedStatus } from '../flow/flowSlice';

const APPLICATION_ERROR =
  'An error occurred while attempting to apply for a new license.';
const DOCUMENT_UPLOAD_ERROR =
  'An unexpected error occurred while attempting to upload documents.';
const PAYMENT_CALCULATION_ERROR =
  'An error occurred while validating licensing fees.';

/**
 * Creates a new license application via the API and stores the application number in Redux.
 * @param data - Application data used to create the license
 * @param accessToken - Auth token for the API request
 * @param rentalStructure - Rental structure config (required/hiddable)
 * @param dispatch - Redux dispatch
 * @returns The new application number, or null on failure
 */
const generateApplication = async (
  data: ApplicationData,
  accessToken: string,
  rentalStructure: RequiredHiddable,
  dispatch: Dispatch<any>
): Promise<string | null> => {
  try {
    const { applicationNumber } = await api.createNewLicense(
      data,
      accessToken,
      rentalStructure
    );
    dispatch(setApplyApplicationNumber(applicationNumber));
    return applicationNumber;
  } catch (err) {
    console.error(APPLICATION_ERROR, err);
    throw new Error(APPLICATION_ERROR);
  }
};

/**
 * Fetches upload tokens per document type, then uploads each document's attached files.
 * No-ops if documents is undefined; skips upload and marks complete if no configs have files.
 * @param applicationNumber - License application number (from generateApplication)
 * @param documents - Map of document type name to document config (required, attachedFiles)
 * @param state - Redux root state (for API access)
 */
const uploadDocuments = async (
  applicationNumber: string,
  documents: Document | undefined,
  state: RootState
): Promise<void> => {
  if (!documents) return;
  const uploadConfiguration: Array<UploadConfiguration> = [];
  try {
    // Upload the documents using the new license number (documents are stored with document type as key)
    const entries = Object.entries(documents);
    for (const [docTypeName, files] of entries) {
      const { uploadToken } = await api.fetchDocumentUploadToken(
        applicationNumber,
        docTypeName,
        {},
        state
      );
      uploadConfiguration.push({
        document: docTypeName,
        uploadToken,
        files,
      });
    }

    const allDocumentsHasFiles = uploadConfiguration.every(
      (config) => config.files.attachedFiles.length > 0
    );

    if (uploadConfiguration.length === 0 || !allDocumentsHasFiles) {
      // If there is no uploadConfiguration, it's empty or not all documents have files,
      // it only contains configs with no associated files, then we're done.
      return;
    }
    // Do the upload
    for (const uploadConfig of uploadConfiguration) {
      const attachedFiles = uploadConfig.files.attachedFiles;
      for (const documentFile of attachedFiles) {
        const { blobFile } = documentFile;
        await api.uploadDocument({
          uploadToken: uploadConfig.uploadToken,
          file: blobFile,
        });
      }
    }
    return;
  } catch (err) {
    throw new Error(DOCUMENT_UPLOAD_ERROR);
  }
};

/**
 * Fetches license fee breakdown for the application, normalizes it (custom breakdown or default),
 * and stores payment ID, fees, signatory, and document-upload-completed in Redux.
 * @param applicationNumber - License application number
 * @param state - Redux root state (config, apply application data)
 * @param dispatch - Redux dispatch
 * @returns Whether the user is required to pay (false if total is 0 or config says no payment)
 */
const calculatePayment = async (
  applicationNumber: string,
  state: RootState,
  dispatch: Dispatch<any>
): Promise<boolean> => {
  try {
    const { details, paymentId, summary, ...rest } =
      await api.calculateLicenseFee(applicationNumber);

    const customBreakdown = getCustomBreakdown(details);
    const chargeableFlow = state?.config?.settings?.express?.registration
      ?.application as ChargeableFlow;
    const licenseVerbiage =
      state?.config?.settings?.express?.registration?.type_verbiage;

    let requiresPayment = checkPaymentRequired(chargeableFlow);

    if (summary.total === 0) {
      requiresPayment = false;
    }

    const breakdown =
      customBreakdown.length > 0
        ? customBreakdown
        : [
            {
              key: 'licenseFee',
              label: `${licenseVerbiage} Fee`,
              value: summary.total,
            },
          ];

    const paymentFee = {
      ...rest,
      breakdown,
      summary,
    };
    const { certifications } = state.apply?.applicationData ?? {};

    dispatch(setPaymentID(paymentId));
    dispatch(setPaymentFees(paymentFee));
    dispatch(setSignatoryName(certifications?.signatory));
    return requiresPayment;
  } catch (err) {
    console.error(PAYMENT_CALCULATION_ERROR, err);
    throw new Error(PAYMENT_CALCULATION_ERROR);
  }
};

export { generateApplication, uploadDocuments, calculatePayment };

/**
 * The application confirmation thunk consist in a four step process:
 * 1. Create the license - Api call to create a new license, this returns an application number which is required
 * for the next steps.
 * 2. Upload the documents - Api call to get the token and  upload the documents, this requires the application number and the documents to upload.
 * 3. Calculate the payment fees - Api call to calculate the payment fees, this requires the application number and the documents to upload.
 * 4. Redirection to the payment page that is handled by the initiator
 * @param data
 * @returns
 */
export const confirmApplication = (
  data: ApplicationData
): AppThunk<void, typeof api> => {
  return async (dispatch, getState) => {
    try {
      const accessToken = getState().auth.accessToken;
      dispatch(startApplication(data));
      const applicationData = getState().apply
        ?.applicationData as ApplicationData;
      const rentalStructure = getState().config.settings?.express?.registration
        .rental_structure as RequiredHiddable;

      // 1. Create the license
      const applicationNumber = await generateApplication(
        applicationData,
        accessToken,
        rentalStructure,
        dispatch
      );
      if (!applicationNumber) return;

      const { documents } = applicationData;
      // 2. Upload the documents
      await uploadDocuments(applicationNumber as string, documents, getState());
      // 3. Calculate the payment fees
      const requiresPayment = await calculatePayment(
        applicationNumber as string,
        getState(),
        dispatch
      );

      // 4, Redirect to the next page
      const selectedCurrentStep = getState().flow.currentStep;
      if (requiresPayment) {
        dispatch(
          setStepCompletedStatus({ stepNum: selectedCurrentStep, status: true })
        );
        dispatch(setCurrentStep(selectedCurrentStep + 1));
      }
      dispatch(finishApplication());
    } catch (err: unknown) {
      if (err instanceof Error) {
        dispatch(setApplicationError(err.message));
      } else {
        dispatch(setApplicationError('An unknown error occurred.'));
      }
      console.error(
        'An error occurred in the process of application creation.',
        err
      );
    }
  };
};

export const getExistingLicensesForAPN = (
  apn: string,
  unitNumber = ''
): AppThunk<void, typeof api> => {
  return async (dispatch, getState, api) => {
    const data = await api.lookupAPNAddress(apn, unitNumber);
    const result = {
      inUse: false,
      details: [],
      hasError: false,
      errorMessage: '',
      isResidential: data?.parcelSpecificInfo?.residential,
    } as {
      inUse: boolean;
      details: Array<{
        type: string;
        registrantName: string;
        permitNumber: string;
        propertyAddress: string;
        expirationDate: string;
      }>;
      hasError: boolean;
      errorMessage: string;
      isResidential: boolean;
    };
    if (data?.code === 'DkNotFound') {
      result.hasError = true;
      result.errorMessage = data.message;
      return result;
    }
    if (data?.licenses && data?.licenses?.length > 0) {
      result.inUse = true;
      result.details = data.licenses.map((l) => {
        return {
          type: l.type,
          registrantName: l.registrant.name,
          permitNumber: l.number,
          propertyAddress: data.address,
          expirationDate: l.registrant.expiryDate,
        };
      });
    }
    return result;
  };
};
